// src/pages/SmartHomeFunctionsPage.js
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { FUNCTION_LIST } from "../lib/constants";
import { canon } from "../lib/seo";

export default function SmartHomeFunctionsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goToProductsFor = (slug) => {
    navigate(`/kategoriak?all=1&functions=${encodeURIComponent(slug)}`);
  };

  const goToDetails = (slug) => {
    navigate(`/funkciok/${slug}`);
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  return (
    <>
      {/* SEO – Functions */}
      <title>Okosotthon funkciók – Solarya</title>
      <meta
        name="description"
        content="Világítás, árnyékolás, fűtés, érzékelők, kamerák és sok más funkció egy helyen. Válaszd ki a funkciót és mutatjuk a szükséges termékeket."
      />
      <link rel="canonical" href={canon(pathname)} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content="Okosotthon funkciók – Solarya" />
      <meta
        property="og:description"
        content="Válaszd ki a funkciót és mutatjuk a szükséges termékeket."
      />
      <meta property="og:url" content={canon(pathname)} />



      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
        {/* HERO */}
        <section className="relative h-[18rem] md:h-[24rem] overflow-hidden mb-10">
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 1.05, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            style={{
              backgroundImage: `url('/img/t5.webp')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "brightness(0.65)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
          <div className="relative h-full max-w-6xl mx-auto px-6 flex flex-col justify-end pb-10 text-white">
            <motion.h1
              className="text-3xl md:text-5xl font-extrabold drop-shadow-lg"
              variants={fadeUp}
              initial="hidden"
              animate="show"
            >
              Okosotthon funkciók
            </motion.h1>
            <motion.p
              className="text-lg text-white/90 max-w-2xl mt-2"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              transition={{ delay: 0.1 }}
            >
              Nézd meg, mit tud egy modern okosotthon: válassz funkciót, és azonnal
              megmutatjuk, milyen termékek kellenek hozzá.
            </motion.p>
          </div>
        </section>

        {/* FUNKCIÓ GRID */}
        <section className="max-w-6xl mx-auto px-6 pb-16">
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={{
              hidden: {},
              show: {
                transition: { staggerChildren: 0.06 },
              },
            }}
          >
            {FUNCTION_LIST.map((f, idx) => (
              <motion.div
                key={f.slug}
                variants={fadeUp}
                className="group bg-white/90 backdrop-blur rounded-2xl border border-gray-200 hover:border-blue-400 hover:shadow-lg transition-all flex flex-col overflow-hidden"
              >
                {/* KÉP */}
                <button
                  onClick={() => goToProductsFor(f.slug)}
                  className="w-full aspect-[4/3] bg-gray-50 overflow-hidden relative"
                >
                  <motion.img
                    src={`/img/base/${f.slug}.webp`}
                    alt={f.title}
                    loading="lazy"
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
                </button>

                {/* SZÖVEG */}
                <div className="flex flex-col justify-between flex-1 p-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800 mb-1">
                      {f.title}
                    </h2>
                    <p className="text-sm text-gray-600 line-clamp-3">{f.desc}</p>
                  </div>

                  {/* AKCIÓK */}
                  <div className="mt-4 flex items-center gap-2">
                    <button
                      onClick={() => goToProductsFor(f.slug)}
                      className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 transition"
                    >
                      Termékek
                    </button>
                    <button
                      onClick={() => goToDetails(f.slug)}
                      className="px-3 py-1.5 rounded-lg border text-sm hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 transition"
                    >
                      Részletek
                    </button>
                  </div>

                  {/* Tipp */}
                  <div className="text-xs text-gray-500 mt-3 border-t pt-2">
                    Tipp: a <strong>„Termékek”</strong> gomb az összes kategóriát
                    megnyitja, és automatikusan beállítja a funkció-szűrőt{" "}
                    <span className="font-medium text-gray-700">„{f.title}”</span> értékre.
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </section>
      </div>
    </>
  );
}
